import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { url_servicios } from 'src/app/config/config';
import { RepositoryV2Service } from './repository.v2.service';
import { WeeklyReportV2 } from '../models/v2/weekly-report.v2.model';
import { ListParameters, ListResponse } from '../models';
import { formatDateToYmd } from 'src/app/shared/utils/formatDate';

@Injectable({ providedIn: 'root' })
export class WeeklyReportsV2Service extends RepositoryV2Service<WeeklyReportV2> {
  constructor() {
    super(inject(HttpClient), url_servicios + '/v2/weekly-reports');
  }

  transform(data: unknown): WeeklyReportV2 {
    return data as WeeklyReportV2;
  }

  /**
   * Fetch all weekly reports for a BIP with bulk parameters
   * @param bipId - The BIP ID to fetch reports for
   * @param dateStart - Start date for filtering (optional)
   * @param dateEnd - End date for filtering (optional)
   * @param perPage - Number of records per page (default: 2500)
   * @returns Observable of weekly reports
   */
  getWeeklyReportsByBipId(
    bipId: number,
    dateStart?: string,
    dateEnd?: string,
    perPage: number = 2500
  ): Observable<ListResponse<WeeklyReportV2>> {
    const options: ListParameters & { bip_id: number } = {
      bip_id: bipId,
      per_page: perPage,
      page: 1,
      cache: false,
      search: ''
    };

    // Add date filters if provided
    if (dateStart) {
      options['date_start'] = dateStart;
    }
    if (dateEnd) {
      options['date_end'] = dateEnd;
    }

    return this.list(options);
  }

  /**
   * Get weekly reports for BIP PDF generation
   * Fetches reports from 6 months ago to today
   * @param bipId - The BIP ID
   * @param endDate - End date (default: today)
   * @returns Observable of weekly reports grouped by plan_id
   */
  getWeeklyReportsForPdfGeneration(
    bipId: number,
    endDate: Date = new Date(),
    months: number = 6
  ): Observable<{ [planId: number]: WeeklyReportV2[] }> {
    const normalizedMonths = Number.isFinite(months) ? Math.max(1, Math.trunc(months)) : 6;

    const end = this.toLocalDate(endDate);

    // Calculate start date (months ago)
    const startDate = new Date(end);
    startDate.setMonth(startDate.getMonth() - normalizedMonths);

    const dateStart = formatDateToYmd(startDate) ?? '';
    const dateEnd = formatDateToYmd(end) ?? '';

    return new Observable(observer => {
      this.getWeeklyReportsByBipId(bipId, dateStart, dateEnd).subscribe({
        next: (response) => {
          // Group weekly reports by plan_id
          const groupedReports: { [planId: number]: WeeklyReportV2[] } = {};

          if (response.data && response.data.length > 0) {
            response.data.forEach(report => {
              if (!groupedReports[report.plan_id]) {
                groupedReports[report.plan_id] = [];
              }
              groupedReports[report.plan_id].push(report);
            });

            // Sort each group by date (oldest first)
            Object.keys(groupedReports).forEach(planId => {
              groupedReports[Number(planId)].sort((a, b) =>
                (formatDateToYmd(a.week_end as string | Date | null | undefined) ?? '').localeCompare(
                  formatDateToYmd(b.week_end as string | Date | null | undefined) ?? ''
                )
              );
            });
          }

          observer.next(groupedReports);
          observer.complete();
        },
        error: (error) => {
          console.error('Error fetching weekly reports for PDF generation:', error);
          observer.error(error);
        }
      });
    });
  }

  private toLocalDate(input: Date): Date {
    const base = input ?? new Date();
    return new Date(base.getFullYear(), base.getMonth(), base.getDate());
  }

  /**
   * Trigger the backend generator for a specific week/patient combo.
   */
  generateWeeklyReport(payload: WeeklyReportsGenerationRequest): Observable<WeeklyReportsGenerationResponse> {
    const URL = url_servicios + '/v2/weekly-reports/generate';
    return this.http.post<WeeklyReportsGenerationResponse>(URL, payload);
  }

  regenerateWeeklyReports(payload: WeeklyReportsRegenerationRequest): Observable<WeeklyReportsRegenerationResponse> {
    const URL = url_servicios + '/v2/weekly-reports/regenerate';
    return this.http.post<WeeklyReportsRegenerationResponse>(URL, payload);
  }

  checkExcel(data: unknown) {
    const URL = url_servicios + '/v2/weekly-reports/check-excel';

    return this.http.post<unknown>(URL, data);
  }

  /**
   * Import weekly report data in bulk.
   * Maps to POST {base}/v2/weekly-reports/import-data
   */
  importDataToWeeklyReport(body: WeeklyReportsImportRequest): Observable<{ status: string } | unknown> {
    console.log("🚀 ~ WeeklyReportsV2Service ~ importDataToWeeklyReport ~ body:", body);
    const URL = url_servicios + '/v2/weekly-reports/import-data';
    return this.http.post<{ status: string } | unknown>(URL, { force_update: true, ...body });
  }
}

// Request body type for import-data endpoint
export interface WeeklyReportsImportRequest {
  category: string;
  patient_id: number;
  values: Array<{ plan_id: number; date: string; value: number | string | null }>;
  force_update?: boolean;
}

export interface WeeklyReportsGenerationRequest {
  patient_id?: number;
  date: string;
  force?: boolean;
}

export interface WeeklyReportsGenerationResponse {
  message: string;
  data?: unknown;
}

export type WeeklyReportsRegenerationTargetType = 'category' | 'plan' | 'point';

export interface WeeklyReportsRegenerationRequest {
  target_type: WeeklyReportsRegenerationTargetType;
  bip_id?: number;
  plan_id?: number;
  weekly_report_id?: number;
  category?: string;
  start_date?: string;
  end_date?: string;
  force?: boolean;
  include_teacher_training?: boolean;
}

export interface WeeklyReportsRegenerationSummary {
  created: number;
  updated: number;
  unchanged: number;
}

export interface WeeklyReportsRegenerationTarget {
  target_type: WeeklyReportsRegenerationTargetType;
  target_id: number;
  category?: string;
  effective_categories?: string[];
  force_update?: boolean;
}

export interface WeeklyReportsRegenerationResult {
  report: {
    id: number;
    plan_id: number;
    week_start: string;
    week_end: string;
    value: number | null;
    total_units?: number | null;
  };
  action: 'created' | 'updated' | 'unchanged';
}

export interface WeeklyReportsRegenerationData {
  target: WeeklyReportsRegenerationTarget;
  summary: WeeklyReportsRegenerationSummary;
  week_start?: string;
  week_end?: string;
  reports?: Array<{
    id: number;
    plan_id: number;
    week_start: string;
    week_end: string;
    value: number | null;
    total_units?: number | null;
    created_at?: string;
    updated_at?: string;
  }>;
  results?: WeeklyReportsRegenerationResult[];
}

export interface WeeklyReportsRegenerationResponse {
  status: string;
  message: string;
  data: WeeklyReportsRegenerationData;
}
